/**
 * Life4Earth V2 — API client for slot-anchored community role
 * invitations (DEV-1193 Phase 2b backend / Phase 4 frontend wire-up).
 *
 * Wraps the four endpoints on /api/community-role-slot-invitations:
 *   POST /                              — admin creates invitation
 *   POST /{assignmentGuid}/respond       — invitee accepts or declines
 *   POST /{assignmentGuid}/resign        — assignee self-resigns
 *   POST /{assignmentGuid}/revoke        — admin pulls back / ends
 *
 * Lazy-imported from workflowSideEffects.ts on the accept/decline
 * paths so the workflow module doesn't drag the slot-invitations
 * bundle into the critical-path build.
 */

import { apiFetch } from "./client";

export interface CreateCommunityRoleSlotInvitationRequest {
  userGuid: string;
  invitationMessage?: string | null;
}

export interface RespondToCommunityRoleSlotInvitationRequest {
  /** "accept" | "decline" */
  action: string;
  /** Optional personal note from the invitee. */
  note?: string | null;
}

export interface ResignFromCommunityRoleSlotRequest {
  /** Optional explanation surfaced to the inviter. */
  reason?: string | null;
}

export interface RevokeCommunityRoleSlotAssignmentRequest {
  /** Optional explanation surfaced to the affected user. */
  reason?: string | null;
}

export interface CommunityRoleSlotAssignmentResponse {
  assignmentGuid: string;
  roleSlotGuid: string;
  communityEntityGuid: string;
  userGuid: string;
  status: string;
  invitedAt: string;
  invitedByUserGuid: string;
  invitationMessage: string | null;
  invitationRespondedAt: string | null;
  responseNote: string | null;
  activatedAt: string | null;
  endedAt: string | null;
  endedByUserGuid: string | null;
  endReason: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Admin creates a pending invitation for a platform user. */
export async function createCommunityRoleSlotInvitation(
  roleSlotGuid: string,
  body: CreateCommunityRoleSlotInvitationRequest,
): Promise<CommunityRoleSlotAssignmentResponse> {
  return apiFetch<CommunityRoleSlotAssignmentResponse>(
    `/api/community-role-slot-invitations?roleSlotGuid=${encodeURIComponent(roleSlotGuid)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

/** Invitee accepts or declines an invitation. */
export async function respondToCommunityRoleSlotInvitation(
  assignmentGuid: string,
  body: RespondToCommunityRoleSlotInvitationRequest,
): Promise<{ message: string; status: string }> {
  return apiFetch<{ message: string; status: string }>(
    `/api/community-role-slot-invitations/${encodeURIComponent(assignmentGuid)}/respond`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

/** Assignee self-resigns from an active assignment. */
export async function resignFromCommunityRoleSlot(
  assignmentGuid: string,
  body: ResignFromCommunityRoleSlotRequest,
): Promise<{ message: string; status: string }> {
  return apiFetch<{ message: string; status: string }>(
    `/api/community-role-slot-invitations/${encodeURIComponent(assignmentGuid)}/resign`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

/** Admin revokes a pending invitation OR ends an active assignment. */
export async function revokeCommunityRoleSlotAssignment(
  assignmentGuid: string,
  body: RevokeCommunityRoleSlotAssignmentRequest,
): Promise<{ message: string; status: string }> {
  return apiFetch<{ message: string; status: string }>(
    `/api/community-role-slot-invitations/${encodeURIComponent(assignmentGuid)}/revoke`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}
